import { MESSENGER } from "@/mappings/messengers";
import { Affiliate } from "@/services/affiliates/Affiliate";
import { ComputedRef, computed, Ref, ref } from "vue";
import { EDITABLE_FIELD } from "./types";

type EditableFieldsMap = Record<EDITABLE_FIELD, any>;

export const VALIDATION_RULES = {
    TEXT: (v: string): boolean => !!v && v.length <= 50,
    EMAIL: (v: string): boolean => !!v && v.length > 1 && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v),
};

function getFieldValue(affiliate: Affiliate, key: EDITABLE_FIELD): any {
    switch (key) {
    case EDITABLE_FIELD.NEWCOMER:
        return !!affiliate.newcomer;
    case EDITABLE_FIELD.MANAGER:
    case EDITABLE_FIELD.CONTRACT_TYPE:
        return affiliate[key]?.id;
    case EDITABLE_FIELD.VERTICALS:
    case EDITABLE_FIELD.TRAFFIC_SOURCES:
    case EDITABLE_FIELD.TAGS:
        return affiliate[key]?.map((i) => +i.id) || [];
    case EDITABLE_FIELD.HOLD_PAYMENTS:
    case EDITABLE_FIELD.HOLD_DAYS:
    case EDITABLE_FIELD.BALANCE: {
        const balance = affiliate.balance;
        if (key === EDITABLE_FIELD.BALANCE) {
            return !!balance?.enabled ? 'enabled' : 'disabled';
        }
        if (key === EDITABLE_FIELD.HOLD_DAYS) {
            return balance?.hold_days;
        }
        if (key === EDITABLE_FIELD.HOLD_PAYMENTS) {
            return balance?.hold_payments;
        }
        return balance?.[key];
    }
    default:
        return affiliate[key];
    }
}

export function useEditableFields(getAffiliate: () => Affiliate): {
    editableFields: Ref<EditableFieldsMap>;
    setCurrentValues: () => void;
    resetFields: (keys: EDITABLE_FIELD[]) => void;
    editItem: (key: EDITABLE_FIELD, value: any) => void;
    getChangedValues: ComputedRef<Record<string, any>>;
    isFieldValid: (rule: (v: string) => boolean, key: EDITABLE_FIELD) => ComputedRef<boolean>;
    isMessengerValid: ComputedRef<boolean>;
} {
    const editableFields: Ref<EditableFieldsMap> = ref({} as EditableFieldsMap);

    const setCurrentValues = () => {
        const affiliate = getAffiliate();
        const values = {} as EditableFieldsMap;

        for (const rawKey in EDITABLE_FIELD) {
            const key = EDITABLE_FIELD[rawKey] as EDITABLE_FIELD;
            values[key] = getFieldValue(affiliate, key);
        }

        editableFields.value = values;
    };

    const resetFields = (keys: EDITABLE_FIELD[]) => {
        const affiliate = getAffiliate();
        const updated = { ...editableFields.value };

        for (const key of keys) {
            updated[key] = getFieldValue(affiliate, key);
        }

        editableFields.value = updated;
    };

    const editItem = (key: EDITABLE_FIELD, value: any) => {
        editableFields.value = { ...editableFields.value, [key]: value };
    };

    const getChangedValues = computed(() => {
        const affiliate = getAffiliate();
        const data: Record<string, any> = {};

        for (const rawKey in EDITABLE_FIELD) {
            const key = EDITABLE_FIELD[rawKey] as EDITABLE_FIELD;
            const newValue = editableFields.value[key];
            const currentValue = affiliate[key];

            switch (key) {
            // getFieldValue отдаёт для этих полей id вложенного объекта,
            // поэтому и сравнивать надо с id, а не с самим объектом.
            case EDITABLE_FIELD.MANAGER:
            case EDITABLE_FIELD.CONTRACT_TYPE:
                if (newValue !== currentValue?.id) {
                    data[key] = newValue;
                }
                break;
            // getFieldValue приводит newcomer к boolean, а в модели он
            // boolean | number — сравнение с сырым значением всегда неравно.
            case EDITABLE_FIELD.NEWCOMER:
                if (newValue !== !!currentValue) {
                    data[key] = newValue;
                }
                break;
            case EDITABLE_FIELD.VERTICALS:
            case EDITABLE_FIELD.TRAFFIC_SOURCES:
            case EDITABLE_FIELD.TAGS: {
                const current = JSON.stringify(newValue);
                const original = JSON.stringify(currentValue?.map((i) => +i.id) || []);
                if (current !== original) data[key] = newValue;
                break;
            }
            case EDITABLE_FIELD.BALANCE:
            case EDITABLE_FIELD.HOLD_PAYMENTS:
            case EDITABLE_FIELD.HOLD_DAYS: {
                const balance = affiliate.balance;

                if (key === EDITABLE_FIELD.BALANCE) {
                    const formatNewValue = newValue === 'enabled';
                    if (formatNewValue !== !!balance?.enabled) {
                        data[key] = newValue;
                    }
                }
                if (key === EDITABLE_FIELD.HOLD_DAYS) {
                    if (newValue !== balance?.hold_days) {
                        data[key] = +newValue;
                    }
                }
                if (key === EDITABLE_FIELD.HOLD_PAYMENTS) {
                    if (newValue !== balance?.hold_payments) {
                        data[key] = +newValue;
                    }
                }
                break;
            }

            default:
                if (newValue !== currentValue) {
                    data[key] = newValue;
                }
            }
        }

        return data;
    });

    const isFieldValid = (rule: (v: string) => boolean, key: EDITABLE_FIELD) => {
        return computed(() => rule(editableFields.value[key]));
    };

    const isMessengerValid = computed(() => {
        const value = editableFields.value[EDITABLE_FIELD.MESSENGER];
        if (value?.type === MESSENGER.TEAMS) {
            return VALIDATION_RULES.EMAIL(value?.account ?? "");
        }
        return true;
    });

    return {
        editableFields,
        setCurrentValues,
        resetFields,
        editItem,
        getChangedValues,
        isFieldValid,
        isMessengerValid,
    };
}
